import { ButtonHTMLAttributes, HTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, forwardRef, useEffect, useId, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../i18n';
import symbolUrl from '../../assets/symbol.svg';

/* ---------- Buttons ---------- */
type Variant = 'primary' | 'tonal' | 'accent' | 'outline' | 'ghost' | 'danger';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'md' | 'lg';
  block?: boolean;
  loading?: boolean;
}
export function Button({ variant = 'primary', size = 'md', block, loading, className = '', children, disabled, ...rest }: ButtonProps) {
  const cls = ['gr-btn', `gr-btn--${variant}`, size === 'lg' && 'gr-btn--lg', block && 'gr-btn--block', className].filter(Boolean).join(' ');
  return (
    <button className={cls} disabled={disabled || loading} {...rest}>
      {loading && <span className="gr-spinner" aria-hidden style={{ width: 16, height: 16, borderWidth: 2 }} />}
      {children}
    </button>
  );
}

/* ---------- Card ---------- */
export function Card({ children, tonal, small, className = '', ...rest }: { children: ReactNode; tonal?: boolean; small?: boolean; className?: string } & HTMLAttributes<HTMLDivElement>) {
  const cls = ['gr-card', tonal && 'gr-card--tonal', small && 'gr-card--sm', className].filter(Boolean).join(' ');
  return <div className={cls} {...rest}>{children}</div>;
}

/* ---------- Fields ---------- */
interface FieldWrapProps { label?: string; hint?: string; error?: string; children: (id: string) => ReactNode }
function FieldWrap({ label, hint, error, children }: FieldWrapProps) {
  const id = useId();
  return (
    <div className="gr-field">
      {label && <label htmlFor={id}>{label}</label>}
      {children(id)}
      {error ? <span className="gr-error-text" role="alert">{error}</span> : hint ? <span className="gr-hint">{hint}</span> : null}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> { label?: string; hint?: string; error?: string; plate?: boolean }
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, hint, error, plate, className = '', ...rest }, ref) {
  return (
    <FieldWrap label={label} hint={hint} error={error}>
      {(id) => <input ref={ref} id={id} className={['gr-input', plate && 'gr-input--plate', className].filter(Boolean).join(' ')} aria-invalid={!!error} {...rest} />}
    </FieldWrap>
  );
});

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> { label?: string; hint?: string; error?: string; options: { value: string; label: string }[] }
export function Select({ label, hint, error, options, ...rest }: SelectProps) {
  return (
    <FieldWrap label={label} hint={hint} error={error}>
      {(id) => (
        <select id={id} className="gr-select" aria-invalid={!!error} {...rest}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </FieldWrap>
  );
}

/* ---------- Feedback ---------- */
export function Badge({ tone = 'default', children }: { tone?: 'default' | 'success' | 'warning' | 'error' | 'accent'; children: ReactNode }) {
  return <span className={`gr-badge ${tone !== 'default' ? `gr-badge--${tone}` : ''}`}>{children}</span>;
}
export function Alert({ tone = 'info', children }: { tone?: 'info' | 'error' | 'success'; children: ReactNode }) {
  return <div className={`gr-alert gr-alert--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>{children}</div>;
}
export function Spinner({ label }: { label?: string }) {
  return (
    <div className="gr-row" role="status" aria-live="polite">
      <span className="gr-spinner" /> {label && <span className="gr-muted">{label}</span>}
    </div>
  );
}
export function PageLoader() {
  const { t } = useTranslation();
  return <div className="gr-main gr-main--narrow" style={{ display: 'grid', placeItems: 'center' }}><Spinner label={t('common.loading')} /></div>;
}

/* ---------- Brand ---------- */
export function Logo({ size = 32, wordmark = true }: { size?: number; wordmark?: boolean }) {
  return (
    <span className="gr-row gr-logo" style={{ gap: 10 }}>
      <img src={symbolUrl} alt="" width={size} height={size} style={{ display: 'block' }} />
      {wordmark && <span className="gr-wordmark" aria-label="greenrinse">green<span>rinse</span></span>}
    </span>
  );
}

/* ---------- Shell ---------- */
/** `section` groups consecutive items under a small heading in the sidebar. */
export interface NavItem { to: string; label: string; icon?: ReactNode; end?: boolean; section?: string }

/**
 * App frame. Desktop: a fixed sidebar with its own scroll (every item stays reachable, the active one is kept in view)
 * next to the page. Below 900 px the sidebar becomes a drawer behind a menu button. The operator PWA uses `bottomNav`.
 * Every route change starts the page at the top; the sidebar keeps its own scroll position.
 */
export function AppShell({ nav, right, children, bottomNav }: { nav?: NavItem[]; right?: ReactNode; children: ReactNode; bottomNav?: boolean }) {
  const sideNav = !!nav && nav.length > 0 && !bottomNav;
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setDrawer(false);
    // Keep the active item visible without jumping the sidebar when it already is.
    sidebarRef.current?.querySelector<HTMLElement>('.gr-nav-link.active')?.scrollIntoView({ block: 'nearest' });
  }, [pathname]);

  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawer(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawer]);

  const sections: { title?: string; items: NavItem[] }[] = [];
  for (const n of nav ?? []) {
    const last = sections[sections.length - 1];
    if (last && last.title === n.section) last.items.push(n); else sections.push({ title: n.section, items: [n] });
  }

  return (
    <div className={`gr-shell ${sideNav ? 'gr-shell--sidenav' : ''}`}>
      {sideNav && (
        <>
          <div className={`gr-drawer-backdrop ${drawer ? 'is-open' : ''}`} onClick={() => setDrawer(false)} aria-hidden />
          <nav ref={sidebarRef} className={`gr-sidenav ${drawer ? 'is-open' : ''}`} aria-label="Main">
            <div className="gr-sidenav__brand">
              <Logo />
              <button type="button" className="gr-icon-btn gr-sidenav__close" onClick={() => setDrawer(false)} aria-label="Close menu"><X size={20} /></button>
            </div>
            <div className="gr-sidenav__scroll">
              {sections.map((sec, i) => (
                <div key={i} className="gr-nav-section">
                  {sec.title && <div className="gr-nav-section__title">{sec.title}</div>}
                  {sec.items.map((n) => (
                    <NavLink key={n.to} to={n.to} end={n.end} className="gr-nav-link">
                      {n.icon && <span className="gr-nav-link__icon" aria-hidden>{n.icon}</span>}
                      <span className="gr-nav-link__label">{n.label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </div>
          </nav>
        </>
      )}
      <div className="gr-content">
        <header className="gr-topbar">
          {sideNav && <button type="button" className="gr-icon-btn gr-hide-desktop" onClick={() => setDrawer(true)} aria-label="Open menu" aria-expanded={drawer}><Menu size={22} /></button>}
          <span className={sideNav ? 'gr-hide-desktop' : ''}><Logo size={28} /></span>
          <span className="gr-topbar__spacer" />
          <div className="gr-topbar__actions">{right}</div>
        </header>
        <main className="gr-main">{children}</main>
        {bottomNav && nav && (
          <nav className="gr-bottomnav" aria-label="Main">
            {nav.map((n) => <NavLink key={n.to} to={n.to} end={n.end}>{n.icon}<span>{n.label}</span></NavLink>)}
          </nav>
        )}
      </div>
    </div>
  );
}

/* ---------- Language switch ---------- */
export function LanguageSwitch() {
  const { i18n } = useTranslation();
  const supported = (i18n.options.supportedLngs || []) as readonly string[];
  return (
    <select
      aria-label="Language"
      className="gr-select"
      style={{ minHeight: 36, width: 'auto', padding: '4px 10px', borderRadius: 999 }}
      value={i18n.resolvedLanguage ?? 'en'}
      onChange={(e) => i18n.changeLanguage(e.target.value)}
    >
      {LANGUAGES.filter((l) => supported.includes(l.code)).map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
    </select>
  );
}
